import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Mic } from "lucide-react";
import { useState } from "react";
import { EmptyState, ErrorState, LoadingState, PageHeading } from "@/components/app/AppUI";
import { AccionesLead, EnlacesContacto, OrigenChip } from "@/components/crm/leads/AccionesLead";
import { FichaLead } from "@/components/crm/leads/FichaLead";
import { CLAVE_PENDIENTES } from "@/components/crm/leads/useGrabaciones";
import { grabacionRepository, leadRepository } from "@/data/repositories";
import { textoPendientes } from "@/domain/grabaciones";
import { ETIQUETA_ETAPA, agruparAgenda, fechaBoda, mensajeWhatsApp } from "@/domain/leads";
import type { AgendaItem } from "@/domain/types";

export const Route = createFileRoute("/_authenticated/app/hoy")({ component: HoyPage });

function HoyPage() {
  const agenda = useQuery({
    queryKey: ["agenda"],
    queryFn: () => leadRepository.agenda(),
    // Los leads nuevos y las reuniones de hoy cambian con la hora.
    refetchInterval: 60_000,
  });
  const [abierto, setAbierto] = useState<string | null>(null);

  if (agenda.isLoading) return <LoadingState label="Preparando tu día" />;
  if (agenda.error) return <ErrorState message={agenda.error.message} />;

  const items = agenda.data ?? [];
  const grupos = agruparAgenda(items);

  return (
    <div className="space-y-7">
      <PageHeading
        eyebrow="Ventas · Hoy"
        title="Hoy"
        description={
          items.length === 1 ? "1 persona pendiente." : `${items.length} personas pendientes.`
        }
        action={
          <Link to="/app/leads" className="btn-secondary">
            Ver tablero <ArrowRight className="size-4" />
          </Link>
        }
      />

      <LlamadasPorAnalizar />

      {grupos.length === 0 ? (
        <section className="panel">
          <EmptyState
            title="Hoy no tienes a nadie pendiente"
            description="Cuando entre un lead o toque una llamada, aparecerá aquí."
          />
        </section>
      ) : (
        grupos.map((grupo) => (
          <section key={grupo.accion} className="panel">
            <div className="panel-header">
              <h2 className="font-display text-xl">{grupo.etiqueta}</h2>
              <span className="font-mono text-xs text-muted-foreground">{grupo.items.length}</span>
            </div>
            {grupo.items.map((item) => (
              <TarjetaHoy
                key={item.request_id}
                item={item}
                alAbrir={() => setAbierto(item.request_id)}
              />
            ))}
          </section>
        ))
      )}

      {abierto && <FichaLead leadId={abierto} alCerrar={() => setAbierto(null)} />}
    </div>
  );
}

/** Aviso discreto: grabaciones subidas que Claude aún no ha resumido. */
function LlamadasPorAnalizar() {
  const pendientes = useQuery({
    queryKey: [CLAVE_PENDIENTES],
    queryFn: () => grabacionRepository.pendientes(),
  });
  const total = pendientes.data?.length ?? 0;
  if (total === 0) return null;
  return (
    <p className="flex items-center gap-2 text-sm text-muted-foreground">
      <Mic className="size-3.5" aria-hidden />
      {textoPendientes(total)} · Pídeselo a Claude
    </p>
  );
}

function TarjetaHoy({ item, alAbrir }: { item: AgendaItem; alAbrir: () => void }) {
  const mensaje = mensajeWhatsApp(item.accion, {
    nombre: item.name,
    lugar: item.location,
    reunion: item.meeting_at,
  });
  return (
    <article className="grid gap-4 border-b border-border p-5 last:border-b-0 lg:grid-cols-[minmax(0,1fr)_auto]">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="text-left text-lg font-semibold hover:text-primary"
            onClick={alAbrir}
          >
            {item.name}
          </button>
          <OrigenChip source={item.source} />
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
            {ETIQUETA_ETAPA[item.stage]}
          </span>
        </div>
        <p className="mt-1 font-semibold text-primary">{item.motivo}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Boda: {fechaBoda(item.event_date)} · {item.location ?? "Lugar sin definir"}
        </p>
        {item.ultima_nota && (
          <p className="mt-2 line-clamp-2 border-l-2 border-border pl-3 text-sm italic text-muted-foreground">
            {item.ultima_nota}
          </p>
        )}
        <div className="mt-4">
          <AccionesLead lead={{ id: item.request_id, name: item.name }} modo="modal" />
        </div>
      </div>
      <div className="lg:pt-1">
        <EnlacesContacto phone={item.phone} mensaje={mensaje} />
      </div>
    </article>
  );
}
